import { getRuntimeChartPalette } from "../../../features/branding/brandingUtils";

// ─── Color Palette ───────────────────────────────────────────────────────────

export const DEFAULT_COLOR_PALETTE = [
  "#ec7a08",
  "#d87007",
  "#c56607",
  "#b15c06",
  "#9d5205",
  "#8a4804",
  "#763d04",
  "#633303",
  "#4f2902",
  "#3b1f01",
  "#281501",
  "#140b00",
];

export function getChartColorPalette() {
    return getRuntimeChartPalette(DEFAULT_COLOR_PALETTE);
}

// ─── Field Resolution ────────────────────────────────────────────────────────

const METRIC_CANDIDATES = ['sum', 'mean', 'avg', 'count', 'min', 'max', 'value'];

/**
 * Resolves which columns from the backend data to use as category and value.
 *
 * Category field: aggregate_by → x_column_name
 * Value field:    aggregation_op (if not 'none') → y_columns[0] → auto-detect from row keys
 */
export function resolveFields(chart, firstRow) {
    const categoryField = chart.aggregate_by || chart.x_column_name;

    let valueField;
    if (chart.aggregation_op && chart.aggregation_op !== 'none') {
        valueField = chart.aggregation_op;
    } else {
        valueField = chart.y_columns?.[0]?.column_name;
    }
    if (!valueField && firstRow) {
        valueField = METRIC_CANDIDATES.find(k => k in firstRow)
            || Object.keys(firstRow).find(k => k !== categoryField);
    }

    return { categoryField, valueField: valueField || 'count' };
}

/**
 * Resolves a human-readable series label from chart config.
 * Priority: y_label → y_column title → schema_verbose_map → raw field name → fallback.
 */
export function resolveGroupLabel(chart, valueField, fallback = 'Dataset') {
    const yCol = chart.y_columns?.[0];
    return chart.y_label
        || yCol?.column_title
        || chart.schema_verbose_map?.[valueField]
        || valueField
        || fallback;
}

// ─── Core Transforms (2) ─────────────────────────────────────────────────────
//
// All Carbon Charts use one of two data shapes:
//   A) {group, value}           — category lives in `group`
//   B) {key, group, value}      — category lives in `key`, `group` is a series label
//
// Every chart in the app maps to one of these two via the backend's
// XYGraphPanel config (x_column_name, aggregate_by, aggregation_op, y_columns).

/**
 * Core transform A: maps backend rows → [{group, value}].
 *
 * `group` = row[categoryField]  (the x-axis / slice / bar label)
 * `value` = row[valueField]     (the numeric measure)
 *
 * Used by: SimpleBar, Pie, Donut, Lollipop, Boxplot, Histogram*, Gauge*, Meter*
 *   (* with small post-processing — see adapters below)
 */
export function transformToGroupValue(panelData, chart) {
    if (!panelData?.length) return [];

    const { categoryField, valueField } = resolveFields(chart, panelData[0]);

    return panelData
        .filter(r => r != null)
        .map(r => {
            const cat = r?.[categoryField];
            const raw = r?.[valueField];
            if (cat == null || raw == null) return null;
            const value = Number(raw);
            if (!Number.isFinite(value)) return null;
            return { group: String(cat), value };
        })
        .filter(Boolean);
}

/**
 * Gauge charts display one aggregated value. Keeping this transform separate
 * makes it possible for every representation of the chart to use that same
 * value instead of the source rows.
 */
export function buildGaugeData(panelData, chart) {
    const total = transformToGroupValue(panelData, chart)
        .reduce((sum, entry) => sum + entry.value, 0);
    return [{ group: 'value', value: total }];
}

/**
 * Core transform B: maps backend rows → [{key, group, value}].
 *
 * `key`   = row[categoryField]           (the x-axis label)
 * `group` = resolveGroupLabel(chart)     (static series label for legend)
 * `value` = row[valueField]              (the numeric measure)
 *
 * Used by: StackedBar, GroupedBar, Line, Area, Scatter*, Bubble*, Sparkline*
 *   (* with small post-processing — see adapters below)
 */
export function transformToKeyGroupValue(panelData, chart) {
    if (!panelData?.length) return [];

    const { categoryField, valueField } = resolveFields(chart, panelData[0]);
    const groupLabel = resolveGroupLabel(chart, valueField);

    return panelData
        .filter(r => r != null)
        .map(r => {
            const cat = r?.[categoryField];
            const raw = r?.[valueField];
            if (cat == null || raw == null) return null;
            const value = Number(raw);
            if (!Number.isFinite(value)) return null;
            return { key: String(cat), group: groupLabel, value };
        })
        .filter(Boolean);
}

// ─── Adapters (thin wrappers for chart-specific needs) ───────────────────────

/**
 * Histogram: expands aggregated bucket counts into individual observations
 * so Carbon Charts can re-bin them.  Input: [{group: bucketLabel, value: count}].
 */
export function expandHistogramBuckets(groupValueData, groupLabel) {
    const out = [];
    groupValueData.forEach(({ group: bucket, value: count }) => {
        const n = Math.min(Math.max(0, Math.round(count)), 10000);
        const bucketNum = Number(bucket);
        if (!Number.isFinite(bucketNum) || n <= 0) return;
        for (let i = 0; i < n; i++) out.push({ group: groupLabel, value: bucketNum });
    });
    return out;
}

/**
 * Sparkline: replaces `key` with synthetic dates for a time-axis sparkline.
 */
export function addSyntheticDates(keyGroupValueData) {
    return keyGroupValueData.map((d, i) => {
        const date = new Date('2024-01-01');
        date.setDate(date.getDate() + i);
        return { group: d.group, date, value: d.value };
    });
}

/**
 * Scatter / Bubble: uses row index as x-position and copies value to radius.
 */
export function addIndexAndRadius(groupValueData) {
    return groupValueData.map((d, i) => ({
        key: i,
        value: d.value,
        radius: d.value,
        group: d.group,
        label: d.group
    }));
}

// ─── Options Builders ────────────────────────────────────────────────────────

export function buildChartTitle(chart) {
    return chart.title?.toUpperCase() || chart.name?.toUpperCase() || '';
}

export function buildXAxisTitle(chart) {
    return chart.x_label?.toUpperCase() || chart.x_column_name?.toUpperCase() || '';
}

export function buildYAxisTitle(chart) {
    return chart.y_label?.toUpperCase()
        || chart.y_columns?.map(a => a.column_name.toUpperCase()).join(', ')
        || '';
}

export function buildXYChartOptions(chart, overrides = {}) {
    const {
        bottomMapsTo = 'key',
        bottomScaleType = 'labels',
        leftMapsTo = 'value',
        leftScaleType = 'linear',
        height = '400px',
        ...rest
    } = overrides;

    return {
        title: buildChartTitle(chart),
        axes: {
            bottom: {
                title: buildXAxisTitle(chart),
                mapsTo: bottomMapsTo,
                scaleType: bottomScaleType,
                truncation: { type: "none"},
                ticks: { rotation: "always"}
            },
            left: {
                title: buildYAxisTitle(chart),
                mapsTo: leftMapsTo,
                scaleType: leftScaleType,
                truncation: { type: "none"},
                ticks: { rotation: "always"}
            }
        },
        height,
        ...rest
    };
}

export function buildColorScale(data, palette = getChartColorPalette()) {
    if (!data || data.length === 0) return {};
    const uniqueGroups = [...new Set(data.map(d => d.group))];
    const scale = {};
    uniqueGroups.forEach((group, idx) => {
        scale[group] = palette[idx % palette.length];
    });
    return scale;
}
